// .js extension so the emitted ESM dist is loadable by raw Node ESM (the unit
// tests import dist/ directly, no bundler) — this is the first runtime relative
// import in this file; bundler-resolution maps .js → .ts at compile time.
import { openaiSdpExchange, heartbeat, endSession } from './realtimeVoiceLib.js';
const DEFAULT_GUIDE_API_TOOL_SPECS = {
    persist_narration: { path: '/realtime/narration', method: 'POST', body: 'flat' },
    narration_near: { path: '/realtime/narration/near', method: 'GET', body: 'query' },
};
export class RealtimeVoiceSession {
    constructor(_config, _deps) {
        this._config = _config;
        this._deps = _deps;
        this._pc = null;
        this._dc = null;
        this._pendingText = null;
        this._mic = null;
        this._audioEl = null;
        this._token = null;
        this._running = false;
        // PTT (Hybrid #1233): the mic sender stays in the SDP but its track is detached
        // (replaceTrack(null)) until the user holds — Phantom-Whisper-Fix.
        this._micSender = null;
        this._micTrack = null;
        this._heartbeatTimer = null;
    }
    get running() {
        return this._running;
    }
    /** Open the realtime session: mint token, connect WebRTC, wire the data channel. */
    async start() {
        if (this._running)
            return;
        if (!this._config.consent) {
            throw new Error('realtime consent required — show the DSGVO disclosure and start with consent:true');
        }
        if (this._config.suppressNarration !== false)
            this._deps.setMode(true);
        try {
            this._token = await this._mintToken();
            const pc = new RTCPeerConnection();
            this._pc = pc;
            if (this._config.inputMode === 'ptt') {
                // Push-to-Talk (Hybrid #1233): capture the mic but keep its sender track
                // DETACHED until the user holds the button. Phantom-Whisper-Fix — a track
                // with enabled=false still streams digital silence that Whisper/Realtime
                // hallucinates phantom questions from (cost + wrong answers underway);
                // replaceTrack(null) sends NO media. turn_detection=null → manual commit.
                this._mic = await navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true },
                });
                this._micTrack = this._mic.getAudioTracks()[0] || null;
                const tr = pc.addTransceiver(this._micTrack, { direction: 'sendrecv' });
                this._micSender = tr.sender;
                await this._micSender.replaceTrack(null); // detached by default (muted)
            }
            else if (this._config.inputMode === 'voice') {
                // Conversation mode: capture the mic (sendrecv) so the user can talk to
                // the guide. Requires a mic-permission prompt.
                this._mic = await navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true },
                });
                this._mic.getTracks().forEach((t) => pc.addTrack(t, this._mic));
            }
            else {
                // Narration mode (DEFAULT, mic-less): receive-only audio. We only need
                // the model's spoken voice; prompts go in as text via the data channel.
                // A recvonly transceiver gives OpenAI the required audio media section
                // WITHOUT getUserMedia → no mic-permission prompt, no AEC, iOS-friendly.
                pc.addTransceiver('audio', { direction: 'recvonly' });
            }
            // Remote model audio → a playsinline <audio> element (iOS-safe). Attached to
            // the DOM + hidden: some mobile browsers won't play a fully detached element.
            const audio = document.createElement('audio');
            audio.autoplay = true;
            audio.setAttribute('playsinline', '');
            audio.style.display = 'none';
            document.body.appendChild(audio);
            this._audioEl = audio;
            pc.ontrack = (e) => {
                audio.srcObject = e.streams[0];
                // Explicit play() + report. This element is created AFTER the async connect,
                // so the Start-tap's transient activation has expired → mobile blocks
                // autoplay-with-sound. On block the app shows a "tap to hear" button that
                // calls resumeAudio() inside a fresh gesture; on success it clears it.
                void audio.play().then(() => {
                    this._log('info', 'remote audio playing');
                    this._deps.onAudioPlaying?.();
                }, (err) => {
                    this._log('warn', `audio autoplay blocked: ${err}`);
                    this._deps.onAudioBlocked?.();
                });
            };
            const dc = pc.createDataChannel('oai-events');
            this._dc = dc;
            dc.onmessage = (e) => void this.handleEvent(typeof e.data === 'string' ? e.data : '');
            // Configure the session for TEXT-prompted audio narration. Minted sessions
            // can default to server-VAD turn_detection — the model then WAITS for mic
            // audio and stays silent on a text prompt + response.create (the exact "no
            // response" symptom). For the mic-less text/ptt modes we set turn_detection
            // to null (we drive responses manually) and ensure audio+text output.
            dc.onopen = () => {
                if (this._config.inputMode !== 'voice') {
                    this._log('info', 'dc open → session.update (GA: type=realtime, output_modalities=audio)');
                    // GA Realtime API: the session object needs `type: 'realtime'`, and AUDIO
                    // output is requested via `output_modalities: ['audio']`. Without this the
                    // model answers a text prompt with TEXT (response.output_text) → no voice.
                    dc.send(JSON.stringify({
                        type: 'session.update',
                        session: {
                            type: 'realtime',
                            output_modalities: ['audio'],
                            instructions: 'Du bist ein gesprochener Reise-Guide. Antworte IMMER gesprochen auf Deutsch, ' +
                                'kurz (2–3 Sätze), lebendig und konkret. Gib NIEMALS JSON, Koordinaten oder ' +
                                'Tool-Argumente als Text aus — Tools rufst du als echten Function-Call auf. ' +
                                'Falls ein Tool leer ist oder fehlschlägt, erzähl einfach natürlich aus deinem ' +
                                'Wissen weiter — erwähne NIEMALS technische Fehler oder dass etwas nicht abrufbar war.',
                            // Register the guide-api tool schemas so the model emits real
                            // function-calls (→ deps.dispatch → guide-api/artrack) instead of
                            // printing the args as text. The mint only sends tool NAMES, not
                            // schemas, so the client registers them here.
                            tools: [
                                {
                                    type: 'function',
                                    name: 'osm_nearby',
                                    description: 'Liefert ECHTE reale Orte (OpenStreetMap) rund um die GPS-Position — ' +
                                        'Geschäfte, Kirchen, Bahnhöfe, Lokale, Sehenswertes. Funktioniert ÜBERALL. ' +
                                        'Rufe das IMMER ZUERST auf, um deine Erzählung in der echten Umgebung zu verankern.',
                                    parameters: {
                                        type: 'object',
                                        properties: {
                                            lat: { type: 'number' },
                                            lon: { type: 'number' },
                                            radius_m: { type: 'number', description: 'Suchradius in Metern, default 1000' },
                                        },
                                        required: ['lat', 'lon'],
                                    },
                                },
                                {
                                    type: 'function',
                                    name: 'narration_near',
                                    description: 'Holt echte vorhandene Narrationen/Infos nahe einer GPS-Position aus dem ' +
                                        'lokalen Wanderlaut-Korpus. Rufe das IMMER ZUERST auf, bevor du über einen Ort erzählst.',
                                    parameters: {
                                        type: 'object',
                                        properties: {
                                            lat: { type: 'number' },
                                            lon: { type: 'number' },
                                            limit: { type: 'number', description: 'max Anzahl Treffer, default 3' },
                                        },
                                        required: ['lat', 'lon'],
                                    },
                                },
                                {
                                    type: 'function',
                                    name: 'pois_near',
                                    description: 'Findet echte Points-of-Interest (OSM/artrack) nahe einer GPS-Position — ' +
                                        'funktioniert ÜBERALL. Nutze das für reale Orte in der Umgebung.',
                                    parameters: {
                                        type: 'object',
                                        properties: {
                                            lat: { type: 'number' },
                                            lon: { type: 'number' },
                                            radius_m: { type: 'number', description: 'Suchradius in Metern, default 1000' },
                                        },
                                        required: ['lat', 'lon'],
                                    },
                                },
                                {
                                    type: 'function',
                                    name: 'knowledge_query',
                                    description: 'Sucht echtes Wissen (Wikipedia / Knowledge-Graph) zu einem Ort, Begriff ' +
                                        'oder Thema. Nutze es für Hintergrund zu einem Ort in der Nähe.',
                                    parameters: {
                                        type: 'object',
                                        properties: { query: { type: 'string', description: 'Suchbegriff/Ort/Thema' } },
                                        required: ['query'],
                                    },
                                },
                            ],
                        },
                    }));
                }
                // Flush a narration prompt that arrived before the channel was open.
                if (this._pendingText) {
                    const t = this._pendingText;
                    this._pendingText = null;
                    this.sendText(t);
                }
            };
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            const answerSdp = await this._exchangeSdp(offer.sdp || '', this._token.model);
            await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
            this._running = true;
            this._startHeartbeat();
            this._log('info', `Realtime session up (model=${this._token.model}, session=${this._token.session_id})`);
        }
        catch (err) {
            this._log('error', `Realtime start failed: ${err}`);
            await this.stop();
            throw err;
        }
    }
    /** Re-attempt audio playback inside a fresh user gesture (mobile autoplay
     *  recovery). Call from a tap handler after onAudioBlocked fired. */
    async resumeAudio() {
        try {
            await this._audioEl?.play();
            this._log('info', 'audio resumed via user gesture');
            this._deps.onAudioPlaying?.();
            return true;
        }
        catch (err) {
            this._log('warn', `resumeAudio failed: ${err}`);
            return false;
        }
    }
    /** Tear down: close the peer connection, stop the mic, restore the mode gate.
     *  `reason` is forwarded to deps.onStopped ('stopped' = app-initiated,
     *  'slot-reaped' = server freed the slot mid-session). */
    async stop(reason = 'stopped') {
        this._running = false;
        if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer);
            this._heartbeatTimer = null;
        }
        // Release the server-side reservation so a device switch works immediately
        // instead of waiting for the orphan-reap TTL (best-effort, keepalive).
        const vsid = this._token?.voice_session_id;
        if (vsid)
            void endSession(vsid);
        try {
            this._dc?.close();
            this._pc?.close();
            this._mic?.getTracks().forEach((t) => t.stop());
            if (this._audioEl) {
                this._audioEl.srcObject = null;
                this._audioEl.remove();
            }
        }
        finally {
            this._dc = null;
            this._pc = null;
            this._mic = null;
            this._micSender = null;
            this._micTrack = null;
            this._audioEl = null;
            if (this._config.suppressNarration !== false)
                this._deps.setMode(false);
            this._deps.onStopped?.(reason);
        }
    }
    /**
     * Handle one data-channel event. Public + isolated so it's unit-testable with
     * fakes. Only `response.function_call_arguments.done` is acted on; everything
     * else (audio deltas, transcripts, …) is ignored — audio is the media track.
     */
    async handleEvent(raw) {
        let ev;
        try {
            ev = JSON.parse(raw);
        }
        catch {
            return;
        }
        // Diagnostics: surface every NON-delta server event so we can see exactly what
        // the realtime server sends back (session.updated, response.created/done, error,
        // …). Deltas are skipped (high-frequency audio/transcript chunks).
        if (ev.type && !ev.type.endsWith('.delta')) {
            const wantDetail = ev.type === 'error' ||
                ev.type.includes('function_call') ||
                ev.type === 'response.output_text.done';
            this._deps.onServerEvent?.(ev.type, wantDetail ? raw.slice(0, 400) : undefined);
        }
        // Transcript of the model's spoken response → surfaced to the app for a text
        // feed + diagnostics (proves the model IS responding, even if audio is mute).
        if ((ev.type === 'response.audio_transcript.delta' ||
            ev.type === 'response.output_audio_transcript.delta') &&
            typeof ev.delta === 'string') {
            this._deps.onTranscript?.(ev.delta, false);
            return;
        }
        if ((ev.type === 'response.audio_transcript.done' ||
            ev.type === 'response.output_audio_transcript.done') &&
            typeof ev.transcript === 'string') {
            this._deps.onTranscript?.(ev.transcript, true);
            return;
        }
        if ((ev.type === 'response.text.delta' || ev.type === 'response.output_text.delta') &&
            typeof ev.delta === 'string') {
            this._deps.onTranscript?.(ev.delta, false);
            return;
        }
        if (ev.type === 'response.output_text.done' && typeof ev.text === 'string') {
            this._deps.onTranscript?.(ev.text, true);
            return;
        }
        if (ev.type !== 'response.function_call_arguments.done' || !ev.name)
            return;
        const callId = ev.call_id || '';
        let args = {};
        if (ev.arguments) {
            try {
                args = JSON.parse(ev.arguments);
            }
            catch {
                this._log('warn', `Bad function args for ${ev.name}: ${ev.arguments}`);
            }
        }
        const route = this._deps.dispatch({ name: ev.name, arguments: args });
        let output = { ok: true };
        if (route.kind === 'backend') {
            const showSpin = route.feedback === 'searching';
            if (showSpin)
                this._deps.setSearching?.(true);
            try {
                output = await this._routeBackend(route, callId);
            }
            catch (err) {
                this._log('warn', `Backend tool ${route.tool} failed: ${err}`);
                output = { error: String(err) };
            }
            finally {
                if (showSpin)
                    this._deps.setSearching?.(false);
            }
        }
        else if (route.kind === 'unknown') {
            output = { error: `unknown tool ${route.tool}` };
        }
        this._deps.onServerEvent?.(`tool_result:${ev.name}`, JSON.stringify(output).slice(0, 280));
        // Always return a function_call_output so the model keeps going.
        this._sendFunctionOutput(callId, output);
    }
    /**
     * Inject a user text turn into the session and ask the model to respond. Used
     * for GPS-triggered proactive narration (and headless smoke). No microphone
     * needed for the prompt itself — the model still answers in voice.
     */
    sendText(text) {
        if (!this._dc || this._dc.readyState !== 'open') {
            // DC not open yet (narrate can fire before the channel finishes opening) —
            // queue it; dc.onopen flushes after the session.update.
            this._pendingText = text;
            return;
        }
        this._dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
        }));
        this._dc.send(JSON.stringify({ type: 'response.create' }));
    }
    /**
     * GPS-triggered proactive narration (Content #1196) — the realtime parallel of
     * the normal pipeline's GPS→narrate step. Prompts the model to narrate the
     * user's current spot using its tools for real data. The model persists via
     * persist_narration → the SAME narration_point corpus, and drives the SAME UI
     * via display-hints, so storage + visuals stay identical to the normal path.
     */
    narrateLocation(loc) {
        const here = `lat ${loc.lat.toFixed(5)}, lon ${loc.lon.toFixed(5)}`;
        const where = loc.city ? `${loc.city} (${here})` : here;
        this.sendText(`Der Nutzer ist gerade hier: ${where}. Rufe ZUERST dein Tool osm_nearby auf, um ` +
            `die ECHTEN realen Orte rund um diese Position zu holen (Geschäfte, Kirchen, Bahnhof, ` +
            `Lokale, Sehenswertes). DANN erzähl gesprochen auf Deutsch in 2–3 kurzen Sätzen — ` +
            `verankere die Erzählung in diesen echten Orten, erfinde nichts. Gib NIEMALS JSON oder ` +
            `Tool-Argumente als Text aus — die Tools rufst du als Function-Call auf.`);
    }
    /**
     * Push-to-Talk gate (Hybrid #1233). On hold → attach the mic track so audio
     * flows; on release → detach (Phantom-Whisper-Fix) AND commit the buffered
     * audio so the model answers (turn_detection=null → manual commit). No-op
     * outside inputMode:'ptt'.
     */
    async setMicActive(on) {
        if (!this._micSender)
            return;
        if (on) {
            if (this._micTrack)
                await this._micSender.replaceTrack(this._micTrack);
        }
        else {
            await this._micSender.replaceTrack(null);
            if (this._dc && this._dc.readyState === 'open') {
                this._dc.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
                this._dc.send(JSON.stringify({ type: 'response.create' }));
            }
        }
    }
    // ── internals ───────────────────────────────────────────────────────────
    /** Liveness heartbeat (~30s) so the server frees a phantom slot fast (#1233). */
    _startHeartbeat() {
        const vsid = this._token?.voice_session_id;
        if (!vsid)
            return;
        this._heartbeatTimer = setInterval(() => {
            void heartbeat(vsid).then((alive) => {
                if (alive === false)
                    void this.stop('slot-reaped'); // slot reaped/released → stop locally
            });
        }, 30000);
    }
    async _mintToken() {
        // session/start takes QUERY params + auth (Content #1196, verified live).
        // consent=true is mandatory (mic→OpenAI DSGVO gate) — start() enforces it.
        const qs = new URLSearchParams({ consent: 'true' });
        if (this._config.model)
            qs.set('model', this._config.model);
        if (this._config.voice)
            qs.set('voice', this._config.voice);
        if (this._config.language)
            qs.set('language', this._config.language);
        if (typeof this._config.trackId === 'number')
            qs.set('track_id', String(this._config.trackId));
        const headers = this._guideAuth(qs);
        const r = await fetch(`${this._config.sessionStartUrl}?${qs.toString()}`, { method: 'POST', headers });
        if (!r.ok) {
            // Diagnose-reich (Alex 2026-06-25): Status + WELCHE Auth gesendet wurde
            // (bestätigt frischen Bundle-Stand) + guide-api-Body. So unterscheidbar:
            // 401 "need JWT or X-API-KEY" = Endpoint-Auth fehlt (kein/stale X-API-KEY);
            // 502 token_mint_failed/upstream_401 = Endpoint OK, Mint lehnt das JWT ab.
            // KEIN Token-Material geloggt — nur Form-Flags.
            const body = await r.text().catch(() => '');
            const sentXApiKey = !!headers['X-API-KEY'];
            const sentBearer = !!headers.Authorization;
            const jwtish = (this._config.authToken || '').startsWith('eyJ');
            throw new Error(`session/start → HTTP ${r.status} [xapikey=${sentXApiKey} bearer=${sentBearer} jwtish=${jwtish}] ${body.slice(0, 300)}`);
        }
        const t = (await r.json());
        if (!t.client_secret || !t.model)
            throw new Error('session/start missing client_secret/model');
        return t;
    }
    /**
     * Build the guide-api auth (Content #1196/#1233). TWO layers, BOTH sent — not
     * either/or: (1) `X-API-KEY` service-trust (+ `user_id`/`user_email` in the
     * query) is the guide-api ENDPOINT auth — the whole app calls guide-api this
     * way; without it session/start returns 401. (2) The `Authorization: Bearer`
     * user-JWT is FORWARDED by guide-api to AiApi's realtime voice-lane for the
     * mint (the lane requires it). The realtime path needs both at once.
     */
    _guideAuth(qs) {
        const headers = {};
        if (this._config.guideApiKey) {
            headers['X-API-KEY'] = this._config.guideApiKey;
            if (this._config.userId)
                qs.set('user_id', this._config.userId);
            if (this._config.userEmail)
                qs.set('user_email', this._config.userEmail);
        }
        if (this._config.authToken) {
            headers.Authorization = `Bearer ${this._config.authToken}`;
        }
        return headers;
    }
    /** The guide_session_id (X-Session-ID for tool calls); falls back to session_id. */
    get _sessionId() {
        return this._token?.guide_session_id || this._token?.session_id || '';
    }
    async _exchangeSdp(offerSdp, model) {
        const secret = this._token?.client_secret || '';
        // Test-Override behält den konfigurierbaren Base; Produktion nutzt CloudV2s
        // portierte openaiSdpExchange → POSTet an /v1/realtime/calls (GA-WebRTC-
        // Endpoint). Das fixt den alten `beta_api_shape_disabled`-Blocker, den
        // `/v1/realtime` auslöste (Realtime-Spike 2026-06-23 → Hybrid #1233).
        if (this._config.openaiRealtimeUrl) {
            const r = await fetch(`${this._config.openaiRealtimeUrl}?model=${encodeURIComponent(model)}`, {
                method: 'POST',
                body: offerSdp,
                headers: { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/sdp' },
            });
            if (!r.ok)
                throw new Error(`OpenAI SDP ${r.status}: ${(await r.text().catch(() => '')).slice(0, 250)}`);
            return r.text();
        }
        return openaiSdpExchange(model, secret, offerSdp);
    }
    /**
     * Route a Read/Persist tool-call to its backend (X-Session-ID stamped).
     * The two backends differ in shape (Content #1196):
     *   - AiApi tool router → POST `{arguments, call_id}` (nested).
     *   - guide-api → per-tool spec: persist = POST flat body, narration_near =
     *     GET with query params.
     */
    async _routeBackend(route, callId) {
        const sid = this._sessionId;
        if (route.target === 'aiapi') {
            const r = await fetch(`${this._config.aiApiToolBase}/${route.tool}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Session-ID': sid },
                body: JSON.stringify({ arguments: route.arguments, call_id: callId }),
            });
            if (!r.ok)
                throw new Error(`${route.tool} → HTTP ${r.status}: ${(await r.text().catch(() => '')).slice(0, 180)}`);
            return r.json();
        }
        // guide-api — shape per tool.
        const specs = { ...DEFAULT_GUIDE_API_TOOL_SPECS, ...(this._config.guideApiToolSpecs || {}) };
        const spec = specs[route.tool];
        if (!spec)
            throw new Error(`no guide-api spec for tool ${route.tool}`);
        const base = `${this._config.guideApiBase}${spec.path}`;
        if (spec.method === 'GET' || spec.body === 'query') {
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(route.arguments)) {
                if (v !== undefined && v !== null)
                    qs.set(k, String(v));
            }
            const headers = { 'X-Session-ID': sid, ...this._guideAuth(qs) };
            const r = await fetch(`${base}?${qs.toString()}`, { method: spec.method, headers });
            if (!r.ok)
                throw new Error(`${route.tool} → HTTP ${r.status}: ${(await r.text().catch(() => '')).slice(0, 180)}`);
            return r.json();
        }
        // POST with a FLAT body (the function-call args spread directly). user_id
        // goes in the query per the app's authFetch convention.
        const authQs = new URLSearchParams();
        const headers = { 'Content-Type': 'application/json', 'X-Session-ID': sid, ...this._guideAuth(authQs) };
        const url = authQs.toString() ? `${base}?${authQs.toString()}` : base;
        const r = await fetch(url, { method: 'POST', headers, body: JSON.stringify(route.arguments) });
        if (!r.ok)
            throw new Error(`${route.tool} → HTTP ${r.status}: ${(await r.text().catch(() => '')).slice(0, 180)}`);
        return r.json();
    }
    /** Send a function_call_output back over the data channel (model unblocks). */
    _sendFunctionOutput(callId, output) {
        if (!this._dc || this._dc.readyState !== 'open')
            return;
        this._dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(output) },
        }));
        // Ask the model to continue speaking with the tool result in context.
        this._dc.send(JSON.stringify({ type: 'response.create' }));
    }
    _log(level, msg) {
        this._deps.log?.(level, msg);
    }
}
//# sourceMappingURL=RealtimeVoiceSession.js.map